import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Trophy, 
  Star, 
  Flame, 
  Award, 
  Target,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { getLocalDateString, getLocalDateStringFormatted, getLocalDate } from '../lib/utils';

const iconMap = {
  Trophy,
  Star,
  Flame,
  Award,
  Target,
  Medal: Trophy,
  CheckCircle2: Target
};

const colorClasses = {
  gold: 'badge-gold',
  silver: 'badge-silver',
  bronze: 'badge-bronze',
  blue: 'badge-blue',
  green: 'badge-green',
  purple: 'badge-purple',
  red: 'badge-red'
};

export default function Achievements() {
  const { getAuthHeaders } = useAuth();
  const [children, setChildren] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [childAchievements, setChildAchievements] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [loading, setLoading] = useState(true);
  const [progressMap, setProgressMap] = useState({});
  const [progressTextMap, setProgressTextMap] = useState({});
  const [childTasks, setChildTasks] = useState([]);

  const fetchData = useCallback(async () => {
    let isMounted = true;
    
    try {
      const [childrenRes, achievementsRes] = await Promise.all([
        fetch(`${API_URL}/api/children`, {
          credentials: 'include',
          headers: getAuthHeaders()
        }),
        fetch(`${API_URL}/api/achievements`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
      ]);

      if (isMounted) {
        if (childrenRes.ok) {
          const childrenData = await childrenRes.json();
          setChildren(childrenData);
          if (childrenData.length > 0) {
            setSelectedChild(childrenData[0].child_id);
          }
        }
        if (achievementsRes.ok) {
          setAchievements(await achievementsRes.json());
        }
      }
    } catch (error) {
      if (isMounted) {
        console.error('Failed to fetch data:', error);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getChild = () => children.find(c => c.child_id === selectedChild);

  const isAchievementEarned = (achievementId) => {
    return childAchievements.some(ca => ca.achievement_id === achievementId);
  };

  const fetchChildAchievements = useCallback(async () => {
    if (!selectedChild) return;
    
    try {
      const response = await fetch(`${API_URL}/api/children/${selectedChild}/achievements`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setChildAchievements(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch child achievements:', error);
    }
  }, [selectedChild, getAuthHeaders]);

  const fetchChildTasks = useCallback(async () => {
    if (!selectedChild) return;
    
    try {
      const response = await fetch(`${API_URL}/api/children/${selectedChild}/tasks`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const responseData = await response.json();
        console.log('Raw tasks from API:', responseData); // Debug log
        
        // Extract tasks from the response object
        let allTasks = [];
        if (responseData && responseData.submissions && Array.isArray(responseData.submissions)) {
          allTasks = responseData.submissions;
        } else if (Array.isArray(responseData)) {
          // Fallback if API returns array directly
          allTasks = responseData;
        } else {
          console.error('Expected submissions array in response, got:', responseData);
          setChildTasks([]);
          return;
        }
        
        // Filter and store only approved tasks for efficiency
        const approvedTasks = allTasks.filter(task => task.status === 'approved');
        console.log('Approved tasks:', approvedTasks); // Debug log
        setChildTasks(approvedTasks);
      } else {
        console.error('Failed to fetch tasks, status:', response.status);
        setChildTasks([]);
      }
    } catch (error) {
      console.error('Failed to fetch child tasks:', error);
      setChildTasks([]);
    }
  }, [selectedChild, getAuthHeaders]);

  const calculateAllProgress = useCallback(async () => {
    if (!selectedChild || achievements.length === 0) return;
    
    // Calculate progress for all achievements in parallel
    const progressPromises = achievements.map(async (achievement) => {
      const progress = await getProgress(achievement);
      const progressText = await getProgressText(achievement, progress);
      return {
        achievementId: achievement.achievement_id,
        progress,
        progressText
      };
    });

    // Wait for all progress calculations to complete
    const progressResults = await Promise.all(progressPromises);
    
    // Convert results back to maps
    const newProgressMap = {};
    const newProgressTextMap = {};
    
    progressResults.forEach(({ achievementId, progress, progressText }) => {
      newProgressMap[achievementId] = progress;
      newProgressTextMap[achievementId] = progressText;
    });
    
    setProgressMap(newProgressMap);
    setProgressTextMap(newProgressTextMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild, achievements]);

  useEffect(() => {
    fetchChildAchievements();
    fetchChildTasks();
  }, [fetchChildAchievements, fetchChildTasks]);

  useEffect(() => {
    calculateAllProgress();
  }, [calculateAllProgress]);

  const getProgressText = async (achievement, progress) => {
    const child = getChild();
    if (!child) return `${Math.round(progress)}% complete`;

    switch (achievement.type) {
      case 'points':
        return `${child.total_points}/${achievement.requirement} points`;
      case 'streak':
        return `${child.current_streak}/${achievement.requirement} day streak`;
      case 'task_completion':
        if (!Array.isArray(childTasks)) {
          console.error('childTasks is not an array in task_completion:', childTasks);
          return `${0}/${achievement.requirement} tasks completed`;
        }
        const approvedCount = childTasks.length;
        return `${approvedCount}/${achievement.requirement} tasks completed`;
      case 'specific_task_completion':
        if (!achievement.task_id) return `${Math.round(progress)}% complete`;
        if (!Array.isArray(childTasks)) {
          console.error('childTasks is not an array in specific_task_completion:', childTasks);
          return `${0}/${achievement.requirement} completions`;
        }
        const specificTaskCount = childTasks.filter(task => 
          task.task_id === achievement.task_id
        ).length;
        return `${specificTaskCount}/${achievement.requirement} completions`;
      case 'specific_task_streak':
        if (!achievement.task_id) return `${Math.round(progress)}% complete`;
        if (!Array.isArray(childTasks)) {
          console.error('childTasks is not an array in specific_task_streak:', childTasks);
          return `${0}/${achievement.requirement} day streak`;
        }
        const specificTasks = childTasks.filter(task => 
          task.task_id === achievement.task_id
        );
        
        // Calculate streak for specific task
        const submissionDates = [...new Set(specificTasks.map(task => 
          getLocalDateString(task.submission_date)
        ))].sort((a, b) => new Date(b) - new Date(a));
        
        let currentStreak = 0;
        const checkDate = getLocalDate();
        
        // Check backwards from today - cache string to avoid repeated conversions
        let currentDateString = getLocalDateString(checkDate);
        while (submissionDates.includes(currentDateString)) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
          currentDateString = getLocalDateString(checkDate);
        }
        
        return `${currentStreak}/${achievement.requirement} day streak`;
      case 'special':
        return 'Manually awarded';
      default:
        return `${Math.round(progress)}% complete`;
    }
  };

  const getProgress = async (achievement) => {
    const child = getChild();
    if (!child) return 0;

    switch (achievement.type) {
      case 'points':
        return Math.min((child.total_points / achievement.requirement) * 100, 100);
      case 'streak':
        return Math.min((child.current_streak / achievement.requirement) * 100, 100);
      case 'task_completion':
        if (!Array.isArray(childTasks)) {
          console.error('childTasks is not an array in getProgress task_completion:', childTasks);
          return 0;
        }
        const approvedCount = childTasks.length;
        return Math.min((approvedCount / achievement.requirement) * 100, 100);
      case 'specific_task_completion':
        if (!achievement.task_id) return 0;
        if (!Array.isArray(childTasks)) {
          console.error('childTasks is not an array in getProgress specific_task_completion:', childTasks);
          return 0;
        }
        const specificTaskCount = childTasks.filter(task => 
          task.task_id === achievement.task_id
        ).length;
        return Math.min((specificTaskCount / achievement.requirement) * 100, 100);
      case 'specific_task_streak':
        if (!achievement.task_id) return 0;
        if (!Array.isArray(childTasks)) {
          console.error('childTasks is not an array in getProgress specific_task_streak:', childTasks);
          return 0;
        }
        const specificTasks = childTasks.filter(task => 
          task.task_id === achievement.task_id
        );
        
        // Calculate streak for specific task
        const submissionDates = [...new Set(specificTasks.map(task => 
          getLocalDateString(task.submission_date)
        ))].sort((a, b) => new Date(b) - new Date(a));
        
        let currentStreak = 0;
        const checkDate = getLocalDate();
        
        // Check backwards from today - cache string to avoid repeated conversions
        let currentDateString = getLocalDateString(checkDate);
        while (submissionDates.includes(currentDateString)) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
          currentDateString = getLocalDateString(checkDate);
        }
        
        return Math.min((currentStreak / achievement.requirement) * 100, 100);
      case 'special':
        // Special achievements are manually awarded, no progress to show
        return 0;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Achievements</h1>
            <p className="text-[#6B7280] mt-1">Collect badges and show off your accomplishments</p>
          </div>

          {children.length > 0 && (
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#6B7280]" />
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger className="w-48 rounded-xl" data-testid="child-selector">
                  <SelectValue placeholder="Select child" />
                </SelectTrigger>
                <SelectContent>
                  {children.map((child) => (
                    <SelectItem key={child.child_id} value={child.child_id}>
                      {child.first_name} {child.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Stats Summary */}
        {selectedChild && (
          <Card className="bg-islamic-green text-white rounded-3xl border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <Trophy className="w-10 h-10 text-[#D4AF37]" />
                <div>
                  <h2 className="text-2xl font-bold font-outfit">
                    {getChild()?.first_name}'s Achievements
                  </h2>
                  <p className="text-white/70">
                    {childAchievements.length} of {achievements.length} earned
                  </p>
                </div>
              </div>
              
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden">
                <div 
                  className="h-full bg-[#D4AF37] rounded-full transition-all duration-500"
                  style={{ width: `${(childAchievements.length / Math.max(achievements.length, 1)) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Children */}
        {children.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <Trophy className="w-16 h-16 text-[#D4AF37]/50 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Children Added</h2>
            <p className="text-[#6B7280]">Add a child to view their achievements.</p>
          </Card>
        ) : (
          <>
            {/* Earned Achievements */}
            {childAchievements.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-[#264653] font-outfit mb-4 flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-[#D4AF37]" />
                  Earned Achievements
                </h2>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {childAchievements.map((ca, index) => {
                    const IconComponent = iconMap[ca.icon] || Trophy;
                    
                    return (
                      <Card 
                        key={ca.achievement_id}
                        className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg overflow-hidden animate-slide-up card-hover"
                        style={{ animationDelay: `${index * 0.05}s` }}
                        data-testid={`achievement-${ca.achievement_id}`}
                      >
                        <div className={`${colorClasses[ca.color] || 'badge-gold'} p-6 text-center`}>
                          <IconComponent className="w-12 h-12 mx-auto mb-2" />
                        </div>
                        <CardContent className="p-4 text-center">
                          <h3 className="font-bold text-[#264653] font-outfit">{ca.name}</h3>
                          <p className="text-xs text-[#6B7280] mt-1">{ca.description}</p>
                          <p className="text-xs text-[#D4AF37] mt-2 font-medium">
                            Earned {getLocalDateStringFormatted(ca.earned_date)}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Locked Achievements */}
            <div>
              <h2 className="text-xl font-bold text-[#264653] font-outfit mb-4 flex items-center gap-2">
                <Target className="w-6 h-6 text-[#6B7280]" />
                Available Achievements
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {achievements.filter(a => !isAchievementEarned(a.achievement_id)).map((achievement, index) => {
                  const IconComponent = iconMap[achievement.icon] || Trophy;
                  const progress = progressMap[achievement.achievement_id] || 0;
                  
                  return (
                    <Card 
                      key={achievement.achievement_id}
                      className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-md overflow-hidden animate-slide-up opacity-75 hover:opacity-100 transition-opacity"
                      style={{ animationDelay: `${index * 0.05}s` }}
                      data-testid={`locked-achievement-${achievement.achievement_id}`}
                    >
                      <div className="bg-gray-100 p-6 text-center">
                        <IconComponent className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      </div>
                      <CardContent className="p-4 text-center">
                        <h3 className="font-bold text-[#264653] font-outfit">{achievement.name}</h3>
                        <p className="text-xs text-[#6B7280] mt-1">{achievement.description}</p>
                        
                        {/* Progress bar */}
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-full bg-[#D4AF37] rounded-full transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <p className="text-xs text-[#6B7280] mt-1">
                            {progressTextMap[achievement.achievement_id] || `${Math.round(progress)}% complete`}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {achievements.filter(a => !isAchievementEarned(a.achievement_id)).length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <Trophy className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
                    <p className="text-xl font-bold text-[#264653] font-outfit">All Achievements Earned!</p>
                    <p className="text-[#6B7280]">Amazing! You've collected all the badges!</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
